import type { Cell } from "./cells";
import type { CellFailure } from "./cell-tenants";

export interface TenantCount {
  tenant_id: string;
  count: number;
}

export interface CellTenantCountsResult {
  cell: Cell;
  items?: TenantCount[];
  error?: Error | null;
  isLoading?: boolean;
}

/**
 * Per-tenant counts from every cell that answered. A cell can hold copies of
 * another cell's tenants, so a tenant known to live in another cell is
 * dropped from this cell's answer; an unknown tenant is kept.
 */
export function mergeCellTenantCounts(
  results: CellTenantCountsResult[],
  tenantCellId: (tenantId: string) => string | undefined,
): {
  counts: Map<string, number>;
  /** The cell each counted tenant came from. */
  tenantCellIds: Map<string, string>;
  failures: CellFailure[];
  loadingCells: Cell[];
} {
  const counts = new Map<string, number>();
  const tenantCellIds = new Map<string, string>();
  const failures: CellFailure[] = [];
  const loadingCells: Cell[] = [];

  for (const { cell, items, error, isLoading } of results) {
    if (error) {
      failures.push({ cell, error });
      continue;
    }
    if (!items) {
      if (isLoading) loadingCells.push(cell);
      continue;
    }
    for (const { tenant_id: tenantId, count } of items) {
      const home = tenantCellId(tenantId);
      if (home && home !== cell.id) continue;
      counts.set(tenantId, (counts.get(tenantId) ?? 0) + count);
      tenantCellIds.set(tenantId, cell.id);
    }
  }

  return { counts, tenantCellIds, failures, loadingCells };
}

export interface CellTenantRowsResult<T extends { tenant_id: string }> {
  cell: Cell;
  rows?: T[];
  error?: Error | null;
  isLoading?: boolean;
}

/**
 * Per-tenant rows from every cell that answered, each tagged with its cell.
 * A row for a tenant known to live in another cell is dropped, as in
 * mergeCellTenantCounts.
 */
export function mergeCellTenantRows<T extends { tenant_id: string }>(
  results: CellTenantRowsResult<T>[],
  tenantCellId: (tenantId: string) => string | undefined,
): {
  rows: (T & { cell: Cell })[];
  failures: CellFailure[];
  loadingCells: Cell[];
} {
  const rows: (T & { cell: Cell })[] = [];
  const failures: CellFailure[] = [];
  const loadingCells: Cell[] = [];

  for (const { cell, rows: cellRows, error, isLoading } of results) {
    if (error) {
      failures.push({ cell, error });
      continue;
    }
    if (!cellRows) {
      if (isLoading) loadingCells.push(cell);
      continue;
    }
    for (const row of cellRows) {
      const home = tenantCellId(row.tenant_id);
      if (home && home !== cell.id) continue;
      rows.push({ ...row, cell });
    }
  }

  return { rows, failures, loadingCells };
}
